import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Headphones, Plus, ExternalLink } from 'lucide-react';
import type { CSLog } from '@shared/types/log';
import type { LogCreatePayload } from '@shared/types/log';
import api from '../../../lib/api';

/** 접수 구분 → 뱃지 색상 */
function getCategoryColor(category: string | null): string {
    if (!category) return 'bg-[#F5F7FA] text-gray-600';
    const map: Record<string, string> = {
        서버: 'bg-[#1ABB9C]/10 text-blue-600',
        원패스: 'bg-green-50 text-green-600',
        비상벨: 'bg-orange-50 text-orange-600',
    };
    return map[category] ?? 'bg-[#F5F7FA] text-gray-600';
}

/** 세팅기간에 "진행중"이 포함되면 진행중, 아니면 완료 */
function getStatusBadge(settingPeriod: string | null): { label: string; className: string } | null {
    if (!settingPeriod) return null;
    if (settingPeriod.includes('진행중') || settingPeriod.endsWith('~')) {
        return { label: '진행중', className: 'bg-orange-100 text-orange-600' };
    }
    return { label: '완료', className: 'bg-green-100 text-green-600' };
}

interface CSLogTabProps {
    logs: CSLog[];
    isLoading: boolean;
    onAdd: (payload: LogCreatePayload) => void;
    isAdding: boolean;
    townId?: number;
}

const emptyForm: LogCreatePayload = {
    receiver: '',
    assignee: '',
    content: '',
    received_date: '',
    setting_period: '',
    requester: '',
    category: '',
};

export default function CSLogTab({ logs, isLoading, onAdd, isAdding, townId }: CSLogTabProps) {
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState<LogCreatePayload>({ ...emptyForm });
    const [errors, setErrors] = useState<string[]>([]);

    const handleChange = (field: keyof LogCreatePayload, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => prev.filter((e) => e !== field));
    };

    const handleSubmit = () => {
        const missing: string[] = [];
        if (!form.receiver.trim()) missing.push('receiver');
        if (!form.assignee.trim()) missing.push('assignee');
        if (!form.content.trim()) missing.push('content');
        if (!form.received_date) missing.push('received_date');
        if (missing.length > 0) {
            setErrors(missing);
            return;
        }
        onAdd(form);
        setForm({ ...emptyForm });
        setShowForm(false);
        setErrors([]);
    };

    const inputClass =
        'w-full px-3 py-2 text-sm border border-[#E6E9ED] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1ABB9C]/20 focus:border-[#1ABB9C] transition';
    const errorInputClass =
        'w-full px-3 py-2 text-sm border border-[#E74C3C]/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition';
    const labelClass = 'block text-[11px] font-medium text-[#ADB5BD] mb-1';

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-bold text-[#2A3F54] uppercase tracking-wider flex items-center gap-2">
                    <Headphones className="w-3.5 h-3.5 text-[#1ABB9C]" /> 단지 관리 메모
                </h3>
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#1ABB9C] text-white rounded-lg hover:bg-[#169F85] transition"
                >
                    <Plus className="w-3 h-3" /> 메모 추가
                </button>
            </div>

            {/* 추가 폼 */}
            {showForm && (
                <div className="mb-5 p-4 border border-[#1ABB9C]/20 bg-[#1ABB9C]/10/30 rounded-xl">
                    <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                            <label className={labelClass}>접수자 *</label>
                            <input
                                type="text"
                                value={form.receiver}
                                onChange={(e) => handleChange('receiver', e.target.value)}
                                className={errors.includes('receiver') ? errorInputClass : inputClass}
                                placeholder="접수자"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>담당자 *</label>
                            <input
                                type="text"
                                value={form.assignee}
                                onChange={(e) => handleChange('assignee', e.target.value)}
                                className={errors.includes('assignee') ? errorInputClass : inputClass}
                                placeholder="담당자"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>접수일 *</label>
                            <input
                                type="date"
                                value={form.received_date}
                                onChange={(e) => handleChange('received_date', e.target.value)}
                                className={errors.includes('received_date') ? errorInputClass : inputClass}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>세팅기간</label>
                            <input
                                type="text"
                                value={form.setting_period ?? ''}
                                onChange={(e) => handleChange('setting_period', e.target.value)}
                                className={inputClass}
                                placeholder="예: 04/18 ~ 진행중"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>요청자</label>
                            <input
                                type="text"
                                value={form.requester ?? ''}
                                onChange={(e) => handleChange('requester', e.target.value)}
                                className={inputClass}
                                placeholder="요청자"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>접수 구분</label>
                            <input
                                type="text"
                                value={form.category ?? ''}
                                onChange={(e) => handleChange('category', e.target.value)}
                                className={inputClass}
                                placeholder="서버, 원패스, 비상벨 등"
                            />
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className={labelClass}>내용 *</label>
                        <textarea
                            rows={2}
                            value={form.content}
                            onChange={(e) => handleChange('content', e.target.value)}
                            className={errors.includes('content') ? errorInputClass : inputClass}
                            placeholder="CS 접수 내용"
                        />
                    </div>
                    {errors.length > 0 && (
                        <p className="text-xs text-[#E74C3C] mb-2">필수 항목을 입력해주세요.</p>
                    )}
                    <div className="flex justify-end gap-2">
                        <button
                            onClick={() => { setShowForm(false); setErrors([]); }}
                            className="px-3 py-1.5 text-xs font-medium border border-[#E6E9ED] rounded-lg hover:bg-[#F5F7FA] transition"
                        >
                            취소
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={isAdding}
                            className="px-3 py-1.5 text-xs font-medium bg-[#1ABB9C] text-white rounded-lg hover:bg-[#169F85] transition disabled:opacity-50"
                        >
                            {isAdding ? '저장 중...' : '추가'}
                        </button>
                    </div>
                </div>
            )}

            {/* 로그 목록 */}
            {isLoading ? (
                <div className="text-center text-sm text-[#ADB5BD] py-8">불러오는 중...</div>
            ) : logs.length === 0 ? (
                <div className="text-center text-sm text-[#ADB5BD] py-8">메모가 없습니다</div>
            ) : (
                <div className="space-y-3">
                    {logs.map((log) => {
                        const status = getStatusBadge(log.setting_period);
                        const isInProgress = status?.label === '진행중';
                        return (
                            <div
                                key={log.id}
                                className={`p-4 rounded-xl transition ${isInProgress
                                    ? 'border border-orange-100 bg-orange-50/30'
                                    : 'border border-[#E6E9ED] hover:shadow-sm'
                                    }`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        {log.category && (
                                            <span
                                                className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${getCategoryColor(log.category)}`}
                                            >
                                                {log.category}
                                            </span>
                                        )}
                                        <span className="text-xs text-[#ADB5BD]">{log.received_date}</span>
                                        {status && (
                                            <span
                                                className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded ${status.className}`}
                                            >
                                                {status.label}
                                            </span>
                                        )}
                                    </div>
                                    {log.setting_period && (
                                        <span className="text-[11px] text-[#ADB5BD]">{log.setting_period}</span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-700 mb-2">{log.content}</p>
                                <div className="flex items-center gap-4 text-[11px] text-[#ADB5BD]">
                                    <span>접수: <span className="text-gray-600">{log.receiver}</span></span>
                                    <span>담당: <span className="text-gray-600">{log.assignee}</span></span>
                                    {log.requester && (
                                        <span>요청: <span className="text-gray-600">{log.requester}</span></span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Redmine 이슈 목록 */}
            {townId && <RedmineIssues townId={townId} />}
        </div>
    );
}

function RedmineIssues({ townId }: { townId: number }) {
    const { data, isLoading } = useQuery({
        queryKey: ['redmine-issues', townId],
        queryFn: async () => {
            const { data } = await api.get(`/towns/${townId}/redmine-issues`);
            return data as { issues: any[]; total: number };
        },
    });

    const redmineBase = 'https://redmine.mytown.cc';

    if (isLoading) return <div className="mt-6 text-center text-sm text-[#ADB5BD] py-4">Redmine 이슈 불러오는 중...</div>;
    if (!data || data.issues.length === 0) return <div className="mt-6 text-center text-sm text-[#ADB5BD] py-4">Redmine 이슈가 없습니다</div>;

    return (
        <div className="mt-6">
            <h3 className="text-xs font-bold text-[#2A3F54] uppercase tracking-wider mb-3 flex items-center gap-2">
                <ExternalLink className="w-3.5 h-3.5 text-[#9B59B6]" /> Redmine C/S 이슈 ({data.total}건)
            </h3>
            <div className="space-y-2">
                {data.issues.map((issue: any) => (
                    <a
                        key={issue.id}
                        href={`${redmineBase}/issues/${issue.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 border border-[#E6E9ED] rounded-lg hover:bg-[#F5F7FA] hover:border-[#1ABB9C]/30 transition"
                    >
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-[#2A3F54]">#{issue.id}</span>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F5F7FA] text-[#73879C]">{issue.status}</span>
                                <span className="text-[10px] text-[#ADB5BD]">{issue.created_on?.slice(0, 10)}</span>
                            </div>
                        </div>
                        <p className="text-sm text-gray-700 truncate">{issue.subject}</p>
                        <div className="mt-1 text-[10px] text-[#ADB5BD]">
                            작성: {issue.author}
                        </div>
                    </a>
                ))}
            </div>
        </div>
    );
}
